/** scratch pad for charts. will be cleaned up */

import { TranslateService } from '@ngx-translate/core';
import { format, timeFormat, timeParse } from 'd3';

import { CoachingEventStatus } from '../../coaching-base/general';
import {
    transformGoalEventsHistoryResponse,
    transformGoalHistoryResponse,
} from '../../coaching-commitment/coaching-commitment.model';
import { GoalEventDataPoint } from '../../coaching-commitment/models/goal-event-data-point.model';
import { GoalHistoryDataPointsResponse } from '../../coaching-commitment/models/goal-history-data-points-response.model';
import { TranslationKeyMap } from '../../coaching-insight/coaching-insight.i18n';
import {
    BaseLineGraphData,
    EventTooltipData,
    GoalTooltipData,
    GraphDefinition,
    GraphPoint,
    LineAreaOptions,
} from '../../coaching-insight/line-area.component';
import {
    addDays,
    getMonthStart,
} from '../../coaching-insight/oa/models/coaching.global.model';
import { RawGraphData } from '../../coaching-insight/oa/models/oa.model';
import { CmpsDecimalPipe } from '../../common/number.pipe';
import {
    MetricDataItem,
    percentToTargetRanked,
} from '../../insight/performance-data/metric.pd.model';
import { PeriodType } from '../../widget/date-range.widget.model';

import { ChartType } from './chart-type.model';

export function processMainData(
    data: RawGraphData,
    graphType: ChartType,
): GraphPoint[] {
    let mainData = [];
    mainData = data.mainData.map((d: any) => {
        const evtdate =
            typeof d.eventDate === 'string'
                ? d.eventDate.split('T')[0]
                : d.eventDate;
        const rank = d.rankOrder === 0 ? -1 : 1;
        let tgt = d.target != null ? d.target : d.actual;
        tgt = tgt === 0 ? 0.00001 : tgt;
        const variance =
            graphType === ChartType.VARIANCE
                ? ((d.actual - tgt) / tgt) * rank * 100
                : d.actual;

        return {
            x: typeof evtdate !== 'string' ? evtdate : parsedtg(evtdate),
            y: graphType === ChartType.VARIANCE ? clamp(variance) : d.actual,
        };
    });

    return mainData.sort((a: GraphPoint, b: GraphPoint) => {
        return a.x - b.x;
    });
}

export function processEventData(
    mainData: GraphPoint[],
    data: RawGraphData,
): GraphPoint[] {
    // base events to the actual metric performance
    let eventData = [];

    if (data.eventData) {
        data.eventData.forEach((a) => {
            const earlierOrEqual = mainData.filter(
                (b) => b.x.getTime() <= a.date.getTime(),
            );
            const tooltipdata: EventTooltipData[] = [
                {
                    eventId: a.eventId,
                    commitmentStatusName: a.commitmentStatusName,
                    date: a.date,
                    eventTypeName: a.eventTypeName,
                    performer: a.performer,
                    statusColour: decideColor(a.commitmentStatusId),
                    eventTypeId: a.eventTypeId,
                    startTime: a.startTime,
                    endTime: a.endTime,
                    goals: a.goals,
                },
            ];

            if (earlierOrEqual.length) {
                eventData.push({
                    x: a.date,
                    y: earlierOrEqual[earlierOrEqual.length - 1].y, // get closest lower date, and get its y value
                    tooltipData: tooltipdata,
                });
            } else {
                if (mainData.length) {
                    // if nothing is earlier than this date, then this is the earliest
                    eventData.unshift({
                        x: a.date,
                        y: mainData[0].y, // get lowest date, and get its y value
                        tooltipData: tooltipdata,
                    });
                }
            }
        });

        // already sorted from earliest to latest date (x)
        eventData = _getUniqueEvents(eventData);
    }

    return eventData;
}

export function processSuperGroupData(data: RawGraphData): GraphPoint[] {
    let supergroupData = [];

    // separate call's data, supergroup
    if (data.sgAvgData.length) {
        // target will be the same
        supergroupData = data.sgAvgData.map((d: any): GraphPoint => {
            const evtdate =
                typeof d.eventDate === 'string'
                    ? d.eventDate.split('T')[0]
                    : d.eventDate;
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            return {
                x: parsedtg(evtdate),
                y: d.actual,
            } as GraphPoint;
        });
        supergroupData.sort((a: GraphPoint, b: GraphPoint) => {
            return a.x - b.x;
        });
    }

    return supergroupData;
}

// TODO: translations will break this. must use StatusId in the GoalEvents API
export function decideColor(statusId: CoachingEventStatus): string {
    switch (statusId) {
        case CoachingEventStatus.ACTIVE:
            return '#137E0F';
        case CoachingEventStatus.PRE_APPROVAL:
        case CoachingEventStatus.UPDATING:
            return '#D17300';
        case CoachingEventStatus.PENDING_CLOSE:
            return '#CE2521';
        case CoachingEventStatus.DRAFT:
        case CoachingEventStatus.CLOSED:
            return '#000';
        default:
            return '';
    }
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function _getUniqueEvents(evtdata: GraphPoint[]) {
    let eventData = [...evtdata];

    if (eventData.length) {
        // get multiples
        const uniquedates = [];
        eventData.forEach((a) => {
            const found = uniquedates.find((b) => {
                b.x.setHours(0, 0, 0, 0);
                a.x.setHours(0, 0, 0, 0);
                return b.x.getTime() === a.x.getTime();
            });
            if (!found) {
                uniquedates.push(a);
            } else {
                found.tooltipData.push(...a.tooltipData);
            }
        });

        eventData = uniquedates;
    }
    return eventData;
}

const parsedtg = timeParse('%Y-%m-%d');
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const clamp = (a: number) =>
    typeof a === 'number' ? Math.min(Math.max(a, -100), 100) : 0;

export function processTargetAndGoalData(
    data: RawGraphData,
    graphType: ChartType,
): { targetData: GraphPoint[]; goalData: GraphPoint[] } {
    let targetData = [];
    let goalData = [];

    // use varianceData, take target property and goal property
    if (graphType === ChartType.TREND) {
        // get all target data from the existing variance data (only ones with values and dates)
        targetData = _processTarget(data);
        goalData = _processGoal(data);
    }

    return {
        targetData,
        goalData,
    };
}

function _processTarget(data: RawGraphData): GraphPoint[] {
    const td = data.mainData.filter((v) => v.target != null);
    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
    if (td && td.length) {
        const targetData = td.map((d: any): GraphPoint => {
            const evtdate =
                typeof d.eventDate === 'string'
                    ? parsedtg(d.eventDate.split('T')[0])
                    : d.eventDate;
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            return {
                x: evtdate,
                y: d.target,
            } as GraphPoint;
        });

        return targetData.sort((a: GraphPoint, b: GraphPoint) => {
            return a.x - b.x;
        });
    }

    return [];
}

function _processGoal(data: RawGraphData): GraphPoint[] {
    // get all goal data from the existing variance data (only ones with values and dates)
    const gd = data.goalData;
    gd.sort(
        (x, y) =>
            new Date(x.date).getTime() - new Date(y.date).getTime() ||
            x.goalId - y.goalId,
    );

    let last: MetricDataItem;
    if (data.mainData.length) {
        // expected to be already sorted
        last = [...data.mainData].reverse()[0];
    }

    return (gd || []).map((d: any, idx: number): GraphPoint => {
        let tooltipData: GoalTooltipData;
        if (last) {
            const rankOrder = last.rankOrder === 0 ? -1 : 1;
            tooltipData = {
                goalValue: d.goalValue,
                // TODO this calculation should be consolidated
                currentPctGoal:
                    d.goalValue === 0 || d.goalValue == null
                        ? null
                        : ((last.actual - d.goalValue) / d.goalValue) *
                              rankOrder +
                          1,
                percentToTarget: percentToTargetRanked(
                    last.actual,
                    last.target,
                    last.rankOrder,
                ),
            };
        }

        const date = d.expectedAchievementDate;
        const evtdate =
            typeof date === 'string' ? parsedtg(date.split('T')[0]) : date;
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        return {
            x: evtdate,
            y: d.goalValue,
            tooltipData,
        } as GraphPoint;
    });
}

export function _genOpts(
    metricName: string,
    graphType: ChartType,
    showEmptyGraph: boolean,
    translateService: TranslateService,
    decimalPipe: CmpsDecimalPipe,
): LineAreaOptions {
    const colourcounter = 0;
    const opts = {
        yTickLabel(d) {
            return d;
        },
        xTickLabel: timeFormat('%b %d'),
        showLegend: false,
        dataLabel: (data: any) => {
            let formaty = '';
            if (data.y == null) {
                return translateService.instant(
                    TranslationKeyMap._711_NO_DATA_AVAILABLE,
                );
            }
            const val = decimalPipe.transform(data.y, '1.2-2');

            if (
                graphType === ChartType.VARIANCE ||
                // eslint-disable-next-line @typescript-eslint/prefer-includes
                (metricName && metricName.indexOf('%') > -1)
            ) {
                formaty = ' %';
            }

            return `${val}${formaty}`; // % variance
        },
        lineStrokeColor: showEmptyGraph
            ? 'transparent'
            : colors[colourcounter].performerColour,
        lineStrokeColor2: showEmptyGraph
            ? 'transparent'
            : colors[colourcounter].performerColour2,
        eventDotsColor: colors[colourcounter].performerColour,
        eventDotsColor2: colors[colourcounter].performerColour2,
        sgLineStrokeColor: colors[colourcounter].supergroupColour,
        tLineStrokeColor: showEmptyGraph
            ? 'transparent'
            : colors[colourcounter].targetColour,
        tLineStrokeColor2: showEmptyGraph
            ? 'transparent'
            : colors[colourcounter].targetColour2,
        gLineStrokeColor: colors[colourcounter].goalColour,
        gLineStrokeColor2: colors[colourcounter].goalColour2,
        showTinyCircles: false,
        showEventCircles: true,
        pendingGoal: false, // this.goalStatusId === GoalStatus.PRE_APPROVAL,
        showVerticalThresholdLines: false,
        // graphLabel: this.metricselected.Description
    };

    const percentageFormat = format('>.2%');

    if (graphType === ChartType.VARIANCE) {
        opts.yTickLabel = function (d) {
            if (!Number.isInteger(d)) {
                // Convert to decimal format and format as percentage with 2 decimal places
                d = percentageFormat(d / 100);
            } else {
                // Otherwise, just convert integer to percentage string
                // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                d = `${d}%`;
            }
            return d;
        };
    }
    return opts;
}

const colors = [
    {
        targetColour: '#137E0F',
        targetColour2: '#137E0F',
        supergroupColour: '#ce221e',
        performerColour: '#1E60D4',
        performerColour2: 'palevioletred',
        goalColour: '#D17300',
        goalColour2: '#363636',
    },
];

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function addLabelsToOpts(
    opts: LineAreaOptions,
    opportunity: number,
    graphType: ChartType,
    periodType: PeriodType,
    showEmptyGraph: boolean,
    periodstartdate: Date,
) {
    const thresholds = { yAxis: null, xAxis: null };

    // red and green area
    const opportunityColor = opportunity ? targetColour : 'transparent';
    const thresoldYAxis = {
        label: 'Target',
        value: 0,
        strokeColor: showEmptyGraph ? 'transparent' : opportunityColor,
        aboveColor: showEmptyGraph ? 'transparent' : 'rgba(83,165,77, 0.8)',
        belowColor: showEmptyGraph ? 'transparent' : 'rgba(255, 173, 64, 0.8)',
    };

    // blue waterfall area
    if (graphType === ChartType.TREND) {
        thresoldYAxis.aboveColor = showEmptyGraph
            ? 'transparent'
            : 'rgba(30, 96, 212, 0.3)';
        thresoldYAxis.belowColor = showEmptyGraph
            ? 'transparent'
            : 'rgba(30, 96, 212, 0.3)';
    }

    thresholds.yAxis = [
        {
            label: 'Opportunity',
            value: opportunity,
            strokeColor: opportunity ? opportunityColour : 'transparent',
            belowColor: showEmptyGraph
                ? 'transparent'
                : 'rgba(231, 65, 65, 0.8)',
        },
    ];

    thresholds.yAxis.unshift(thresoldYAxis);

    thresholds.xAxis = [
        {
            label: 'Curr. Period Start',
            value: periodstartdate,
            strokeColor: periodStartColour,
        },
    ];

    opts.thresholds = thresholds;

    opts.showTinyCircles =
        !showEmptyGraph &&
        ![PeriodType.ROLLING_30, PeriodType.DAY].includes(periodType);

    opts.periodType = periodType;
    return opts;
}

const targetColour = '#7a7a7a';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const supergroupColour = '#ce221e';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const performerColour = '#7a7a7a';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const goalColour = '#4ba337';
const periodStartColour = '#345ea3';
const opportunityColour = '#FF0000';

export function setupChart(
    metricName: string,
    metricId: number,
    graphType: ChartType,
    periodTypeId: PeriodType,
    showEmptyGraph: boolean,
    translateService: TranslateService,
    decimalPipe: CmpsDecimalPipe,
): LineAreaOptions {
    let opts: LineAreaOptions;

    opts = _genOpts(
        metricName,
        graphType,
        showEmptyGraph,
        translateService,
        decimalPipe,
    );

    const periodstartdate =
        periodTypeId === PeriodType.ROLLING_30
            ? addDays(new Date(), -30)
            : getMonthStart(new Date());

    let opportunity = 0;

    if (metricId) {
        opportunity = 0.01; // oaThresholdValue

        if (opportunity > 0) {
            opportunity *= -1;
        }
    }

    opts = addLabelsToOpts(
        opts,
        opportunity,
        graphType,
        periodTypeId,
        showEmptyGraph,
        periodstartdate,
    );
    return opts;
}

const generateEmptyStateData = (): any[] => {
    const r = [0, 1, 2, 3, 4, 5, 6];
    return r.map((a, idx) => {
        return {
            target: 0,
            actual: 3,
            eventDate: addDays(new Date(), a),
        };
    });
};

export function defaultGraphSet(which: ChartType): GraphDefinition[] {
    const ret = createGraphSet(which, [], [], null, []);
    // eslint-disable-next-line no-return-assign
    ret.forEach((e) => (e.showEmptyGraph = false));
    return ret;
}

export function createGraphSet(
    which: ChartType,
    userSg: MetricDataItem[],
    sg: MetricDataItem[],
    goalHistory: GoalHistoryDataPointsResponse,
    goalEventHistory: GoalEventDataPoint[],
): GraphDefinition[] {
    const showEmptyGraph =
        (!userSg || userSg.length === 0) && (!sg || sg.length === 0);
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    let data = {
        mainData: [...generateEmptyStateData()],
        goalData: [],
        targetData: [],
        sgAvgData: [],
        eventData: [],
    } as RawGraphData;

    if (!showEmptyGraph) {
        if (which === ChartType.VARIANCE) {
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            data = {
                mainData: userSg,
                sgAvgData: sg,
                showSg: false,
            } as RawGraphData;
        }
        if (which === ChartType.TREND) {
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            data = {
                mainData: userSg,
                sgAvgData: sg,
                showSg: false,
                component: undefined,
            } as RawGraphData;
        }
        if (goalHistory) {
            data.goalData =
                transformGoalHistoryResponse(goalHistory).datapoints;
        }
        if (goalEventHistory) {
            data.eventData =
                transformGoalEventsHistoryResponse(goalEventHistory);
        }
    }

    const dNew: BaseLineGraphData = {};
    if (data.mainData) {
        dNew.mainData = processMainData(data, which);
        dNew.eventData = processEventData(dNew.mainData, data);
    }
    const tg = processTargetAndGoalData(data, which);
    dNew.targetData = tg.targetData;
    dNew.goalData = tg.goalData;
    if (which !== ChartType.VARIANCE) {
        dNew.supergroupData = processSuperGroupData(data);
    }

    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    const graphdef = {
        data: dNew,
        showEmptyGraph,
    } as GraphDefinition;

    let graphSet = [];
    graphSet = [...graphSet, graphdef];

    //     let opts: LineAreaOptions;
    //     // if (!metric) {
    //     //     return null;
    //     // }
    //     opts = _genOpts(/*metric*/ null, action.payload.which, false);
    //     // this.checkAddLabels(metric, shouldAddLabels, hasGoals);
    // const periodstartdate = action.payload.periodTypeId === PeriodType.ROLLING_30
    //         ? addDays(new Date(), -30)
    //         : getMonthStart(new Date());
    //
    //     let opportunity = 0;
    //     // if (this.activeMetric && this.activeMetric.metric.metricId) {
    //     if (action.payload.metricId) {
    //         // const oppExists = this.oppList.find(x => x.metricId.toString() === this.activeMetric.metric.metricId.toString());
    //         // opportunity = oppExists ? 0.01 : null; // hardcode
    //         opportunity = 0.01;
    //
    //         if (opportunity > 0) {
    //             opportunity *= -1;
    //         }
    //     }
    //
    //     opts = addLabelsToOpts(opts, opportunity, action.payload.which, action.payload.periodTypeId, false, periodstartdate)
    //     graphdef.options = opts;

    return graphSet;
}
